// dependencies
import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import { View } from "react-native";
import { useIntl } from "react-intl";

// components
import { Col, Row } from "@cloudspire/legacy-resources/src/components/Grid";
import {
  Col as Col2,
  Row as Row2,
} from "@cloudspire/legacy-resources/src/components/Grid2";
import HighlightOverlay from "@cloudspire/legacy-resources/src/components/HighlightOverlay";
import Heading from "@cloudspire/legacy-resources/src/components/Heading";
import Breadcrumb from "@cloudspire/legacy-resources/src/components/Breadcrumb";
import Button from "@cloudspire/legacy-resources/src/components/Button2";
import Image from "@cloudspire/legacy-resources/src/components/Image";
import Carousel from "@cloudspire/legacy-resources/src/components/Carousel";
import RoomAddress from "@cloudspire/legacy-resources/src/components/RoomAddress";
import RoomFeatureList from "@cloudspire/legacy-resources/src/components/RoomFeatureList";
import RoomHandicapList from "@cloudspire/legacy-resources/src/components/RoomHandicapList";
import RoomMetaList from "@cloudspire/legacy-resources/src/components/RoomMetaList";
import RoomMovieList from "@cloudspire/legacy-resources/src/components/RoomMovieList";
import RoomOptionList from "@cloudspire/legacy-resources/src/components/RoomOptionList";
import RoomPictureList from "@cloudspire/legacy-resources/src/components/RoomPictureList";
import RoomPieceList from "@cloudspire/legacy-resources/src/components/RoomPieceList";
import RoomTagList from "@cloudspire/legacy-resources/src/components/RoomTagList";
import RoomOwner from "@cloudspire/legacy-resources/src/components/RoomOwner";
import RoomPricing from "@cloudspire/legacy-resources/src/components/RoomPricing";
import RoomCard, {
  CarouselLeftNavigation,
  CarouselRightNavigation,
} from "@cloudspire/legacy-resources/src/components/RoomCard";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import Text from "@cloudspire/legacy-resources/src/components/Text2";
import UIContainer from "@cloudspire/legacy-resources/src/components/UIContainer";
import {
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@cloudspire/legacy-resources/src/components/Modal";
import HasSeenRoom from "../../components/HasSeenRoom";

// containers
import RoomCommentList from "./RoomCommentList";
import LastSeenRoomContainer from "../LastSeenRoom";
import ReadOnlyBookingDatePickerContainer from "@cloudspire/legacy-resources/src/containers/ReadOnlyBookingDatePicker";
import { BookingAside as BookingAsideContainer } from "@cloudspire/legacy-resources/src/containers/Booking";
import RoomSEO from "./SEO";

// constants
import {
  BOOKING_WORKFLOW_VALUE_NATIVE,
  BOOKING_SOURCE_VALUE_WEB,
  BOOKING_NATURE_VALUE_PAR,
} from "@cloudspire/legacy-resources/src/constants/booking";
import theming from "@cloudspire/legacy-resources/src/constants/theming";

// hocs
import useViewportMedia from "@cloudspire/legacy-resources/src/hocs/useViewportMedia";

// contexts
import { useRoute } from "@cloudspire/legacy-resources/src/contexts/route";
import { useConfiguration } from "@cloudspire/legacy-resources/src/contexts/configuration";
import { useRouters } from "@cloudspire/legacy-resources/src/contexts/routers";

// libraries
import { generateUri, get } from "@cloudspire/legacy-shared/src/libraries";
import createModal from "@cloudspire/legacy-resources/src/libraries/utils/createModal";

// helpers
import {
  roomHasMovies,
  roomHasPictures,
  roomHasPieces,
  roomHasOptions,
  roomHasHandicaps,
  roomHasReviews,
  roomHasChildren,
  roomHasMap,
  roomDisplayMap,
  roomGenerateBreacrumbItemList,
  roomIsNatureParent,
  roomFindBestRoomUsingCapacity,
  roomIsAllInclusiveRate,
} from "@cloudspire/legacy-resources/src/helpers/room";
import bookingDelegationGetChannelId from "@cloudspire/legacy-resources/src/helpers/booking/delegation/getChannelId";
import roomGetTrophyList from "@cloudspire/legacy-resources/src/helpers/room/getTrophyList";

// hooks
import { useResponse } from "@cloudspire/legacy-resources/src/hooks/fetcher";

// api
import { roomRoomPage } from "@cloudspire/legacy-resources/src/api/room";
import { pricingEstimateRoomPage } from "@cloudspire/legacy-resources/src/api/pricing";
import { bookingEstimateAside } from "@cloudspire/legacy-resources/src/api/booking";
import { useAuth } from "@cloudspire/legacy-resources/src/contexts/AuthContext";

const { useTheme } = theming;

const RoomMap = dynamic(
  () => import("@cloudspire/legacy-resources/src/components/RoomMap"),
  {
    ssr: false,
  }
);

function RoomContainer(props) {
  const { roomReference, roomSlug } = props;

  const { frontRouter } = useRouters();

  const intl = useIntl();

  const route = useRoute();

  const theme = useTheme();

  const configuration = useConfiguration();

  const query = route.getUri().getParsedQuery();

  const auth = useAuth();

  const { matches } = useViewportMedia({ queries: [{ minWidth: 1100 }] });

  const { data: roomShow } = useResponse(
    roomRoomPage({
      referenceOrSlug: roomSlug,
      configuration,
      checkin: query?.checkin,
    })
  );

  const roomReferenceList = roomShow.data ? [roomReference] : null;
  if (roomIsNatureParent({ room: roomShow.data })) {
    for (const roomChild of roomShow.data?.children ?? []) {
      roomReferenceList.push(roomChild.reference);
    }
  }

  const { data: pricingEstimateSearch } = useResponse(
    pricingEstimateRoomPage({
      roomReferenceList,
      channelId: bookingDelegationGetChannelId({
        configuration,
        room: roomShow.data,
      }),
    })
  );

  const adults = get(query, "capacity", "1");

  const bestRoomReference = roomIsNatureParent({ room: roomShow.data })
    ? roomFindBestRoomUsingCapacity({
        roomList: roomShow.data.children,
        capacity: adults,
      })?.reference ?? ""
    : roomShow?.data?.reference;

  const [parameterList, setParameterList] = useState({
    roomReference: bestRoomReference,
    source: BOOKING_SOURCE_VALUE_WEB,
    workflow: BOOKING_WORKFLOW_VALUE_NATIVE,
    adults,
    checkin: query?.checkin ?? "",
    checkout: query?.checkout ?? "",
    nature: query?.nature ?? BOOKING_NATURE_VALUE_PAR,
    customerHotelSelectionId: query?.customerHotelSelectionId,
    optionList: [],
  });

  const hydrated = useRef(false);
  useEffect(
    function () {
      if (!hydrated.current) {
        if (roomShow) {
          setParameterList((parameterList) => ({
            ...parameterList,
            channelId: bookingDelegationGetChannelId({
              configuration,
              room: roomShow.data,
            }),
          }));
          hydrated.current = true;
        }
      }
    },
    [roomShow, configuration]
  );

  const bookingEstimateResponse = useResponse(
    bookingEstimateAside({
      room: roomShow.data,
      parameterList,
    })
  );

  /**
   * Gère la modification d'un champs du formulaire.
   */
  const handleChange = useCallback(
    function (event) {
      const newParameterList = {
        ...parameterList,
      };

      if ("checkinCheckout" === event.target.name) {
        const checkin = event.target.value.start
          ? event.target.value.start.format("Y-m-d")
          : "";

        const checkout = event.target.value.end
          ? event.target.value.end.format("Y-m-d")
          : "";

        newParameterList.checkin = checkin;
        newParameterList.checkout = checkout;
      } else if ("roomReference" === event.target.name) {
        newParameterList[event.target.name] = event.target.value;

        const selectedRoom =
          parameterList?.roomReference === roomShow.data.reference
            ? roomShow.data
            : roomShow.data.children.find(
                (childRoom) =>
                  childRoom.reference === parameterList?.roomReference
              );

        if (
          newParameterList.adults &&
          (newParameterList.adults < selectedRoom?.minCapacity ||
            newParameterList.adults > selectedRoom?.maxCapacity)
        ) {
          delete newParameterList.adults;
        }
      } else if ("checkbox" === event.target.type) {
        newParameterList[event.target.name] = event.target.checked
          ? event.target.value
          : undefined;
      } else {
        newParameterList[event.target.name] = event.target.value;
      }

      setParameterList(newParameterList);
    },
    [parameterList, roomShow.data]
  );

  /**
   * Gère le clic sur le planning d'une sous-structure.
   */
  function handlePressSeePlanning({ room }) {
    createModal({
      intl,
      theme,
      configuration,
      auth,
      children: (shouldClose) => (
        <ModalBody>
          <ModalHeader onShouldClose={shouldClose}>
            <Text style={{ color: "#ffffff" }}>{room.name}</Text>
          </ModalHeader>

          <ModalContent>
            <div
              style={{
                padding: "1rem",
                display: "flex",
              }}
            >
              <ReadOnlyBookingDatePickerContainer
                channelId={configuration.channel.id}
                roomReference={room.reference}
              />
            </div>
          </ModalContent>
        </ModalBody>
      ),
    });
  }

  /**
   * Gère le clic sur le bouton "Plus d'informations" d'une sous-structure.
   */
  function handlePressAbout({ room }) {
    createModal({
      intl,
      theme,
      configuration,
      auth,
      children: (shouldClose) => (
        <ModalBody>
          <ModalHeader onShouldClose={shouldClose}>
            <Text style={{ color: "#ffffff" }}>{room.name}</Text>
          </ModalHeader>

          <ModalContent>
            <div
              style={{
                padding: "1rem",
                display: "flex",
                flexDirection: "column",
                minWidth: "18.75rem",
              }}
            >
              {roomHasPictures({ room }) && (
                <div>
                  <Carousel
                    shouldAutoplay={false}
                    leftNavigation={<CarouselLeftNavigation />}
                    rightNavigation={<CarouselRightNavigation />}
                  >
                    {room.pictureList.map((media, index) => {
                      return (
                        <View
                          key={index}
                          style={{
                            width: "100%",
                          }}
                        >
                          <Image
                            resizeMode="cover"
                            ratio={9 / 16}
                            source={{ uri: media.urls.large }}
                          />
                        </View>
                      );
                    })}
                  </Carousel>

                  <Stack marginTop={1} />
                </div>
              )}

              <div>
                <Stack marginTop={1} />

                <RoomFeatureList featureListByType={room.featureListByType} />

                <Stack marginTop={1} />
              </div>
            </div>
          </ModalContent>
        </ModalBody>
      ),
    });
  }

  const allInclusiveRate = roomIsAllInclusiveRate({ room: roomShow.data });

  const baseQueryParameters = {
    checkin: query?.checkin,
    checkout: query?.checkout,
    nature: query?.nature,
    capacity: query?.capacity,
  };

  return (
    <>
      {roomShow.data && (
        <>
          <RoomSEO roomReference={roomReference} roomSlug={roomSlug} />

          <Stack marginTop={1} />

          <UIContainer>
            <Breadcrumb
              itemList={roomGenerateBreacrumbItemList({
                intl,
                room: roomShow.data,
                route,
                configuration,
                router: frontRouter,
              })}
            />

            <Stack marginTop={1} />

            <Row
              breakpointList={{ sm: 1100, md: Infinity }}
              col={{ sm: 1, md: 3 }}
              style={{ ...(!matches && { marginRight: 0 }) }}
              gutter={1}
            >
              <Col span={{ sm: 1, md: 3 }}>
                {roomHasPictures({ room: roomShow.data }) && (
                  <RoomPictureList
                    pictureList={roomShow.data.pictureList}
                    altFallback={roomShow.data?.name}
                  />
                )}
              </Col>

              <Col span={{ sm: 1, md: 2 }}>
                <Heading
                  level={1}
                  size="3em"
                  style={{
                    color: "#3a485f",
                    fontWeight: "500",
                  }}
                >
                  {roomShow.data.name}
                </Heading>

                <Stack marginTop={0.5} />

                <View>
                  <Row2 gutter={0.5} type="inline" flexWrap={true}>
                    <Col2 style={{ flexShrink: 1 }}>
                      <Text>
                        {intl.formatMessage(
                          {
                            defaultMessage:
                              "Référence de l’hébergement : # {roomReference}",
                          },
                          {
                            roomReference: roomShow.data.reference,
                          }
                        )}
                      </Text>
                    </Col2>

                    {roomShow.data.registrationNumber && (
                      <Col2>
                        <Text>
                          {intl.formatMessage(
                            {
                              defaultMessage:
                                "N° d’enregistrement : {registrationNumber}",
                            },
                            {
                              registrationNumber:
                                roomShow.data.registrationNumber,
                            }
                          )}
                        </Text>
                      </Col2>
                    )}

                    <Col2>
                      <RoomAddress
                        href={generateUri({
                          name: "App.Action.RoomList",
                          router: frontRouter,
                          parameters: {
                            lang: intl.locale,
                          },
                          query: {
                            ...baseQueryParameters,
                            city: roomShow.data.city,
                            lat: roomShow.data.lat,
                            lng: roomShow.data.lng,
                          },
                        })}
                        zipcode={roomShow.data.zipcode}
                        city={roomShow.data.city}
                      />
                    </Col2>
                  </Row2>
                </View>

                <Stack marginTop={1} />

                <div style={{ backgroundColor: "#f7f7f7", padding: "1rem" }}>
                  <RoomTagList
                    room={roomShow.data}
                    baseQueryParameters={baseQueryParameters}
                    color={roomShow.data.bestColor}
                    categoryList={roomShow.data.categoryList}
                    ranking={roomShow.data.ranking}
                    star={roomShow.data.star}
                    recentlyAdded={roomShow.data.recentlyAdded}
                    rate={roomShow.data.rate}
                    allInclusiveRate={allInclusiveRate}
                    reviews={roomShow.data.reviews}
                    trophyList={roomGetTrophyList({ room: roomShow.data })}
                  />
                </div>

                <Stack marginTop={2} />

                <View>
                  <Heading
                    level={2}
                    size="1.625em"
                    style={{
                      color: "#3a485f",
                      fontWeight: "500",
                    }}
                  >
                    {roomShow.data.title}
                  </Heading>
                </View>

                <Stack marginTop={2} />

                <View>
                  <RoomMetaList
                    flux="detail"
                    arePetsAllowed={roomShow.data.arePetsAllowed}
                    chambers={roomShow.data.chambers}
                    beds={roomShow.data.beds}
                    capacity={roomShow.data.maxCapacity}
                    surface={roomShow.data.surface}
                    categoryList={roomShow.data.categoryList}
                    handicapList={roomShow.data.handicapList}
                    hostTable={roomShow.data.hostTable}
                  />
                </View>

                <Stack marginTop={1.5} />

                <View>
                  <Text style={{ whiteSpace: "break-spaces" }}>
                    {roomShow.data.about}
                  </Text>
                </View>

                <Stack marginTop={1} />

                <View>
                  <Text style={{ whiteSpace: "break-spaces" }}>
                    {roomShow.data.infos}
                  </Text>
                </View>

                <Stack marginTop={1} />

                {roomHasMovies({ room: roomShow.data }) && (
                  <RoomMovieList movieList={roomShow.data.movieList} />
                )}

                {roomHasMap({ room: roomShow.data }) &&
                  roomDisplayMap({ room: roomShow.data }) && (
                    <>
                      <RoomMap
                        lat={roomShow.data.lat}
                        lng={roomShow.data.lng}
                        dragging={false}
                        scrollWheelZoom={false}
                        touchZoom={false}
                        doubleClickZoom={false}
                        zoomControl={false}
                      />

                      <Stack marginTop={1} />
                    </>
                  )}

                <View>
                  <Stack marginTop={1} />

                  <RoomFeatureList
                    featureListByType={roomShow.data.featureListByType}
                  />
                </View>

                <Stack marginTop={2} />

                {roomHasChildren({ room: roomShow.data }) && (
                  <>
                    <View style={{ padding: ".5em" }}>
                      <Heading
                        level={3}
                        size="1.625em"
                        style={{
                          color: "#3a485f",
                          fontWeight: "500",
                        }}
                      >
                        {intl.formatMessage({
                          defaultMessage: "Détail des chambres",
                        })}
                      </Heading>

                      <Stack marginTop={1} />

                      <Row2 type="column" gutter={1}>
                        {roomShow.data.children.map((roomChild) => {
                          return (
                            <Col2 key={roomChild.reference}>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <RoomCard flux="inline" room={roomChild} />
                                </div>

                                <Stack marginTop={0.5} />

                                <Row2
                                  type="inline"
                                  flexWrap={true}
                                  gutter={0.5}
                                >
                                  <Col2>
                                    <Button
                                      appearance="classic"
                                      label={intl.formatMessage({
                                        defaultMessage: "Voir le planning",
                                      })}
                                      onPress={() =>
                                        handlePressSeePlanning({
                                          room: roomChild,
                                        })
                                      }
                                    />
                                  </Col2>

                                  <Col2>
                                    <Button
                                      appearance="classic"
                                      label={intl.formatMessage({
                                        defaultMessage: "Plus d’informations",
                                      })}
                                      onPress={() =>
                                        handlePressAbout({
                                          room: roomChild,
                                        })
                                      }
                                    />
                                  </Col2>

                                  <Col2>
                                    <RoomPricing
                                      pricing={
                                        pricingEstimateSearch.data?.[
                                          roomChild.reference
                                        ]
                                      }
                                    />
                                  </Col2>
                                </Row2>
                              </div>
                            </Col2>
                          );
                        })}
                      </Row2>
                    </View>

                    <Stack marginTop={1} />
                  </>
                )}

                {roomHasPieces({ room: roomShow.data }) && (
                  <>
                    <View style={{ padding: ".5em" }}>
                      <RoomPieceList pieceList={roomShow.data.pieceList} />
                    </View>

                    <Stack marginTop={1} />
                  </>
                )}

                {roomHasOptions({ room: roomShow.data }) && (
                  <>
                    <View
                      style={{ backgroundColor: "#f7f7f7", padding: ".5em" }}
                    >
                      <Heading
                        level={3}
                        size="1.625em"
                        style={{
                          color: "#3a485f",
                          fontWeight: "500",
                        }}
                      >
                        {intl.formatMessage({
                          defaultMessage: "Services supplémentaires",
                        })}
                      </Heading>

                      <Stack marginTop={0.5} />

                      <RoomOptionList
                        name="optionList"
                        optionList={roomShow.data.optionList}
                        value={parameterList?.optionList ?? []}
                        errorList={bookingEstimateResponse.error}
                        shouldRenderInput={false}
                        onChange={handleChange}
                      />
                    </View>

                    <Stack marginTop={1} />
                  </>
                )}

                {roomHasHandicaps({ room: roomShow.data }) && (
                  <>
                    <View style={{ padding: ".5em" }}>
                      <RoomHandicapList
                        handicapList={roomShow.data.handicapList}
                      />
                    </View>

                    <Stack marginTop={1} />
                  </>
                )}

                <View style={{ backgroundColor: "#f7f7f7", padding: ".5em" }}>
                  <Heading
                    level={3}
                    size="1.625em"
                    style={{
                      color: "#3a485f",
                      fontWeight: "500",
                    }}
                  >
                    {intl.formatMessage({
                      defaultMessage: "Le propriétaire",
                    })}
                  </Heading>

                  <Stack marginTop={0.5} />

                  <RoomOwner room={roomShow.data} />
                </View>

                <Stack marginTop={1} />

                {roomHasReviews({ room: roomShow.data }) && (
                  <div id="reviews">
                    <RoomCommentList room={roomShow.data} />
                  </div>
                )}
              </Col>

              <Col span={{ sm: 0, md: 1 }} visible={{ sm: false, md: true }}>
                <div
                  style={{
                    position: "sticky",
                    maxHeight: "calc(100vh - 2rem)",
                    overflowY: "auto",
                    top: "1rem",
                    padding: "1rem",
                    borderRadius: "0.25rem",
                    borderStyle: "solid",
                    borderColor: "#cfdadd",
                    borderWidth: "0.0625rem",
                  }}
                >
                  <BookingAsideContainer
                    type="room"
                    room={roomShow.data}
                    bookingEstimateResponse={bookingEstimateResponse}
                    parameterList={parameterList}
                    onChange={handleChange}
                  />
                </div>
              </Col>
            </Row>
          </UIContainer>

          <HasSeenRoom
            preventRoomReference={roomShow.data.reference}
            roomToAdd={roomShow.data}
          >
            <LastSeenRoomContainer
              preventRoomReference={roomShow.data.reference}
              roomToAdd={roomShow.data}
            />
          </HasSeenRoom>

          {!matches && (
            <HighlightOverlay>
              <BookingAsideContainer
                type="room"
                room={roomShow.data}
                bookingEstimateResponse={bookingEstimateResponse}
                parameterList={parameterList}
                shouldRenderLegend={false}
                onChange={handleChange}
              />
            </HighlightOverlay>
          )}
        </>
      )}
    </>
  );
}

RoomContainer.propTypes = {
  roomReference: PropTypes.string,
  roomSlug: PropTypes.string,
};

export default RoomContainer;
